import React, { useState, useEffect } from 'react'
import TextArea from '../../components/form/TextArea/TextArea'
import { Form, Spinner } from 'react-bootstrap'
import SearchBySelect from '../../components/form/SearchBySelect/SearchBySelect'
import { FormInput } from '../../components/form/FormInput/FormInput'
import { SearchResultTable } from '../../components/kyc/SearchResultTable'
import NewAndCloseButtons from '../../components/form/NewAndCloseButtons/NewAndCloseButtons'
import {
  SEARCH_BY_SELECT_OPTIONS,
  INVALID_REG_NUMBER_WARNING,
  DISCLAIMER,
  RESTRICTED_REPORT,
  PRICELIST,
  INVALID_ID_WARNING,
  INVALID_VAT_WARNING,
} from '../../utils/Constants'
import '../../components/kyc/kycStyle.css'
import './CipcReport.css'
import { ReportResponseForm } from '../../components/kyc/ReportResponseForm'
import FooterButtons from '../../components/form/FooterButtons/FooterButtons'
import {
  validateCompanyRegistrationNumber,
  validateIdNumber,
  validateVATNumber,
} from '../../utils/Validation'
import Alert from '../../components/form/Alerts/BasicAlert'
import {
  downloadReport,
  handleXpertBusinessLoadReport,
  handleXpertBusinessSearchCompany,
} from '../../api/businessEnquiry/businessEnquiry'
import { ProductIds, ReportTypes } from '../../api/apiConstants'
import ScreenLayout from '../../components/layouts/ScreenLayout'
import { useDownloadFile } from '../../hooks/use-download-file'

interface HandleXpertBusinessSearchCompanyProps {
  companyRegistrationNumber: string
  companyName: string
  companyVATNumber: string
  companySoleProprietorId: string
  searchParam: string
  setXpertBusinesses?: any
  setShowProgress: React.Dispatch<React.SetStateAction<boolean>>
  setIsError: (isError: boolean) => void
  setErrorMessage: (errorMessage: string) => void
}

interface Business {
  enquiryId: string
  enquiryResultId: string
  registrationNo: string
}

type Props = {
  setShow: (show: boolean) => void
  setIsError: (isError: boolean) => void
  setErrorMessage: (errorMessage: string) => void
  initialState: any
  setInitialState: (initialState: any) => void
  KYCAccess: boolean
}

const CipcReport: React.FC<Props> = ({
  setShow,
  setIsError,
  setErrorMessage,
  initialState,
  setInitialState,
  KYCAccess,
}) => {
  const [reference, setReference] = useState('')
  const [showProgress, setShowProgress] = useState(false)
  const [cipcSearchParam, setCipcSearchParam] = useState('REG_NR')
  const [companyRegistrationNumber, setCompanyRegistrationNumber] = useState('')
  const [companyVATNumber, setCompanyVATNumber] = useState('')
  const [companySoleProprietorId, setCompanySoleProprietorId] = useState('')
  const [companyName, setCompanyName] = useState('')
  const [showDownloadReport, setShowDownloadReport] = useState(false)
  const [xpertBusinesses, setXpertBusinesses] = useState([])
  const [consent1CIPC, setFirstConsentCIPC] = useState(false)
  const [consent2CIPC, setSecondConsentCIPC] = useState(false)
  const [consent3CIPC, setThirdConsentCIPC] = useState(false)
  const [isValid, setIsValid] = useState(false)
  const [reportId, setReportId] = useState(0)
  const [selectedOption, setSelectedOption] = useState('')

  const handleStartOver = () => {
    setCipcSearchParam('REG_NR')
    setCompanyRegistrationNumber('')
    setCompanyVATNumber('')
    setCompanySoleProprietorId('')
    setCompanyName('')
    setShowDownloadReport(false)
    setXpertBusinesses([])
    setReference('')
  }

  const handleOptionChange = (event: {
    target: { value: React.SetStateAction<string> }
  }) => {
    setSelectedOption(event.target.value)
    if (selectedOption === 'option1') {
      setFirstConsentCIPC(true)
      setSecondConsentCIPC(false)
    } else if (selectedOption === 'option2') {
      setFirstConsentCIPC(false)
      setSecondConsentCIPC(true)
    }
  }

  const handleSubmit = () => {
    const isValid = validateCIPCFields((missingFields: string[]) => {
      if (missingFields.length > 0) {
        alert(
          'Please fill in the following fields: ' + missingFields.join(', '),
        )
      }
    })
    if (!isValid) return
    const trimmedRegistrationNumber = companyRegistrationNumber.replace(
      /\s/g,
      '',
    )
    updateAppState()
    const params: HandleXpertBusinessSearchCompanyProps = {
      companyRegistrationNumber: trimmedRegistrationNumber,
      companyName,
      companyVATNumber,
      companySoleProprietorId,
      searchParam: cipcSearchParam,
      setShowProgress,
      setIsError,
      setErrorMessage,
      setXpertBusinesses,
    }
    handleXpertBusinessSearchCompany(params)
  }

  const handleDownload = (business: Business) => {
    const params = {
      setShowProgress,
      setIsError,
      setErrorMessage,
      business,
      reference,
      setReportId,
      setShowDownloadReport,
      reportTypeId: ReportTypes.CIPCBusinessReport.reportTypeId,
      productId: ProductIds.XpertBusinessReport.productId,
      downloadReport,
      purpose: 'CIPC report',
    }
    handleXpertBusinessLoadReport(params)
  }

  useEffect(() => {
    setCompanyRegistrationNumber('')
    setCompanySoleProprietorId('')
    setCompanyVATNumber('')
    setCompanyName('')
  }, [cipcSearchParam])

  const { mutate: downloadReport, isLoading: isDownloading } = useDownloadFile(
    '/BusinessEnquiry/download-report',
    reportId + '',
    ReportTypes.CIPCBusinessReport.reportTypeId,
    setIsError,
    setErrorMessage,
  )

  const handleDownloadCipcReport = () => {
    const params = {
      reportId,
      setShowProgress,
      setIsError,
      setErrorMessage,
      reportTypeId: ReportTypes.CIPCBusinessReport.reportTypeId,
    }
    downloadReport
  }

  const updateAppState = () => {
    setInitialState({
      ...initialState,
      cipcReport: {
        companyName: companyName,
        companyRegistrationNumber: companyRegistrationNumber,
        companySoleProprietorId: companySoleProprietorId,
        companyVATNumber: companyVATNumber,
      },
    })
  }
  const validateCIPCFields = (
    handleMissing?: (missingFields: string[]) => void,
  ) => {
    let valid = true

    const missingFields = []

    if (
      cipcSearchParam === 'COMP_NAME' &&
      (companyName === '' || companyName.trim() === '')
    ) {
      valid = false
      missingFields.push('Company Name')
    }

    if (
      cipcSearchParam === 'REG_NR' &&
      (companyRegistrationNumber === '' ||
        companyRegistrationNumber.trim() === '')
    ) {
      valid = false
      missingFields.push('Company Registration Number')
    }

    if (
      cipcSearchParam === 'SOLE_PROP' &&
      (companySoleProprietorId === '' || companySoleProprietorId?.trim() === '')
    ) {
      valid = validateIdNumber(companySoleProprietorId!)
      if (!companySoleProprietorId) missingFields.push('Sole Proprietor ID')
    }

    if (
      cipcSearchParam === 'VAT_NR' &&
      (companyVATNumber === '' || companyVATNumber.trim() === '')
    ) {
      valid = validateVATNumber(companyVATNumber)
      if (!companyVATNumber) missingFields.push('VAT Number')
    }

    if (handleMissing) handleMissing(missingFields)
    if (isValid !== valid) setIsValid(valid)

    return valid
  }

  const subHeader = (
    <span>
      Information about companies and organisations registered in South Africa{' '}
      <br />
      from the very latest data as supplied by the Companies and Intellectual{' '}
      <br />
      Property Commission (CIPC).
    </span>
  )

  return (
    <ScreenLayout
      price={PRICELIST.CIPC}
      headerText="CIPC Report"
      subHeaderText={subHeader}
      sampleReport={'CIPC.pdf'}
    >
      {showProgress && (
        <>
          <div className="spinner-container">
            <Spinner
              as="span"
              animation="border"
              variant="dark"
              role="status"
              aria-hidden="true"
            ></Spinner>
            <span> </span>
          </div>
        </>
      )}
      <Form.Group>
        {Object.keys(xpertBusinesses).length === 0 ? (
          <>
            <Form.Group>
              {KYCAccess !== true && (
                <p className="lbl-red my-1">{RESTRICTED_REPORT}</p>
              )}
              <SearchBySelect
                className="mb-3"
                options={SEARCH_BY_SELECT_OPTIONS}
                onChangeHandler={setCipcSearchParam}
              />
              {cipcSearchParam === 'REG_NR' && (
                <FormInput
                  className="mb-3"
                  onChangeHandler={setCompanyRegistrationNumber}
                  value={companyRegistrationNumber}
                  placeHolderText="Registration number"
                  controlId="KYCForm.ControlInput2"
                  required
                />
              )}
              {cipcSearchParam === 'REG_NR' &&
                companyRegistrationNumber &&
                !validateCompanyRegistrationNumber(
                  companyRegistrationNumber,
                ) && (
                  <Alert variant="danger" text={INVALID_REG_NUMBER_WARNING} />
                )}
              {cipcSearchParam === 'VAT_NR' && (
                <>
                  <FormInput
                    autoFocus
                    className="mb-3"
                    onChangeHandler={setCompanyVATNumber}
                    value={companyVATNumber}
                    placeHolderText="VAT number"
                    controlId="KYCForm.ControlInput2"
                    required
                  />
                  {companyVATNumber && !validateVATNumber(companyVATNumber) && (
                    <Alert variant="danger" text={INVALID_VAT_WARNING} />
                  )}
                </>
              )}

              {cipcSearchParam === 'SOLE_PROP' && (
                <>
                  <FormInput
                    className="mb-3"
                    onChangeHandler={setCompanySoleProprietorId}
                    value={companySoleProprietorId}
                    placeHolderText="ID Number"
                    controlId="KYCForm.ControlInput2"
                    required
                  />
                  {companySoleProprietorId &&
                    !validateIdNumber(companySoleProprietorId) && (
                      <Alert variant="danger" text={INVALID_ID_WARNING} />
                    )}
                </>
              )}

              {cipcSearchParam === 'COMP_NAME' && (
                <FormInput
                  className="mb-3"
                  onChangeHandler={setCompanyName}
                  value={companyName}
                  placeHolderText="Company name"
                  controlId="KYCForm.ControlInput2"
                  required
                />
              )}
            </Form.Group>
            <Form.Group
              className="buttonContainer"
              controlId="KYCForm.concent2_chk1"
            >
              <FormInput
                className="mb-3"
                onChangeHandler={setReference}
                value={reference}
                placeHolderText="Search reference (optional)"
                controlId="formReference"
              />
              {!showDownloadReport && (
                <>
                  <FooterButtons
                    setShow={setShow}
                    handleSearch={handleSubmit}
                  />
                  <TextArea className="blackParagraph" Text={DISCLAIMER} />
                </>
              )}
            </Form.Group>
          </>
        ) : (
          <>
            <div style={{ overflow: 'auto' }}>
              <SearchResultTable
                showProgress={showProgress}
                xpertBusinesses={xpertBusinesses}
                handleXpertBusinessLoadReport={(business) =>
                  handleDownload(business)
                }
              />
            </div>
            <br />
            <NewAndCloseButtons
              handleAction={handleStartOver}
              handleClose={setShow}
            />
          </>
        )}
      </Form.Group>
    </ScreenLayout>
  )
}
export default CipcReport
